import type { v2 } from '@deque/billing-service-client';
import { type Subscription } from '@deque/billing-utils';
import type { FeatureFlagId } from '@deque/feature-flags';
import {
  HIDDEN_USER_MANAGEMENT_PRODUCTS,
  isSubscriptionExpired,
  ProductSlugs
} from '~/common/constants';
import { type FeatureFlag } from '~/common/contexts/featureFlags';

// NOTE: Subscriptions to pre-8.0 monitor instances have their `instance_url` set to null
// Those subscriptions should be filtered out to prevent pre-8.0 monitor customers utilizing axe account user management
export const isUnmigratedMonitorSubscription = (subscription: Subscription) => {
  return (
    subscription.product_slug === ProductSlugs.axeMonitor &&
    !(subscription as v2.EnterpriseSubscription).instance_url
  );
};

const productFeatureFlag: Partial<Record<ProductSlugs, FeatureFlagId>> = {
  //make Axe Reports visible in constants.ts but filter it from products with the feature flag
  //remove these changes and the axe_reports_teams_v1 feature flag in a future release
  [ProductSlugs.axeReports]: 'axe_reports_teams_v1',
  [ProductSlugs.axeAuditor]: 'axe_auditor_user_management_v1'
};

const featureFlagDependentProducts = Object.keys(productFeatureFlag);

const isHiddenByFeatureFlag = (
  productSlug: string,
  featureFlags: FeatureFlag[]
) => {
  return (
    featureFlagDependentProducts.includes(productSlug) &&
    !featureFlags.find(
      f => f.id === productFeatureFlag[productSlug as ProductSlugs]
    )?.state
  );
};

export const getHiddenProductSlugsFromSubs = (
  subs: Subscription[],
  getProductBySlug: (slug: string) => v2.Product | undefined,
  featureFlags: FeatureFlag[],
  excludedProductSlugs: ProductSlugs[] = [] //explicitly added product slugs to hide
): ProductSlugs[] => {
  return subs.reduce((acc, sub) => {
    const product = getProductBySlug(sub.product_slug);
    const isProductHiddenByFeatureFlag = isHiddenByFeatureFlag(
      sub.product_slug,
      featureFlags
    );
    const isExcluded =
      excludedProductSlugs.length &&
      excludedProductSlugs.includes(product?.slug as ProductSlugs);

    if (
      product &&
      (isProductHiddenByFeatureFlag ||
        isExcluded ||
        (isSubscriptionExpired(sub) && !product.has_free_tier) ||
        HIDDEN_USER_MANAGEMENT_PRODUCTS.includes(
          product.slug as ProductSlugs
        ) ||
        isUnmigratedMonitorSubscription(sub))
    ) {
      acc.push(product?.slug as ProductSlugs);
    }
    return acc;
  }, [] as ProductSlugs[]);
};
